/**
 * @component ry-logo-bar
 * @category  display
 * @summary   Trust-strip logo bar for landing pages (ry-ui-only). Lays out children as a centered row (default), a horizontal scroll strip (`layout="scroll"`), or an infinite-loop marquee (`layout="marquee"`). Marquee mode wraps the logo children in a track + clones them 3× for a seamless loop; an optional leading `<p>` heading is preserved outside the track.
 *
 * @attribute layout   'scroll' | 'marquee' ?   default=(unset: static wrapping row)   Choose the layout mode. Static = wraps; scroll = horizontal scroll no wrap; marquee = infinite scroll loop.
 *
 * @content   Free-form children. An optional first `<p>` becomes the heading (kept outside the marquee track). Everything else is treated as a logo item — typically `<span>` text, `<img>`, or `<ry-logo>`.
 *
 * @dom (default / scroll)
 *   ry-logo-bar[layout?]
 *     > {authored children}
 *
 * @dom (marquee)
 *   ry-logo-bar[layout="marquee"]
 *     > p   (optional heading — preserved)
 *     > div.ry-logo-bar__track   [data-ry-target="track"]
 *       > {logo children, originals}
 *       > {3 cloned sets, each cloned child gets aria-hidden="true"}
 *
 * @gotcha  Marquee duplication runs at setup time. Adding logos after mount does NOT extend the marquee — re-mount the element. Removing logos also doesn't update the track.
 *
 * @gotcha  Marquee mode REPLACES innerHTML (lifts heading out, then re-appends + appends track). Any nested handlers or non-element children outside the heading are lost.
 *
 * @gotcha  The 3 cloned sets get `aria-hidden="true"` so screen readers don't announce the same logo names 4 times. Only the original set is in the accessible tree.
 *
 * @gotcha  Without any non-`<p>` children, marquee mode is a silent no-op (no track inserted, heading untouched).
 *
 * @example basic-wrapping
 * ```html
 * <ry-logo-bar>
 *   <p>Trusted by industry leaders</p>
 *   <span>Acme</span>
 *   <span>Globex</span>
 *   <span>Initech</span>
 * </ry-logo-bar>
 * ```
 *
 * @example horizontal-scroll
 * ```html
 * <ry-logo-bar layout="scroll">
 *   <span>Acme</span><span>Globex</span><span>Initech</span>
 * </ry-logo-bar>
 * ```
 *
 * @example marquee
 * ```html
 * <ry-logo-bar layout="marquee">
 *   <p>Powering teams everywhere</p>
 *   <span>Acme</span>
 *   <span>Globex</span>
 *   <span>Initech</span>
 * </ry-logo-bar>
 * ```
 *
 * @tutorial default-layout-preserves-children
 * ```js
 * const el = mount(`
 *   <ry-logo-bar>
 *     <p>Trusted by</p>
 *     <span>A</span>
 *     <span>B</span>
 *   </ry-logo-bar>
 * `);
 * await expectEqual(el.children.length, 3, 'all three children preserved');
 * await expectEqual(el.querySelector('[data-ry-target="track"]'), null, 'no track in default layout');
 * ```
 *
 * @tutorial scroll-layout-also-preserves-children
 * ```js
 * const el = mount(`
 *   <ry-logo-bar layout="scroll">
 *     <span>A</span>
 *     <span>B</span>
 *   </ry-logo-bar>
 * `);
 * await expectEqual(el.children.length, 2);
 * await expectEqual(el.querySelector('[data-ry-target="track"]'), null);
 * ```
 *
 * @tutorial marquee-wraps-logos-in-track-and-clones
 * ```js
 * const el = mount(`
 *   <ry-logo-bar layout="marquee">
 *     <span>A</span>
 *     <span>B</span>
 *   </ry-logo-bar>
 * `);
 * const track = el.querySelector('[data-ry-target="track"]');
 * await expectEqual(!!track, true, 'track inserted');
 * // 2 originals + 3 sets of 2 clones = 8 children total
 * await expectEqual(track.children.length, 8, '4× duplication');
 * ```
 *
 * @tutorial marquee-preserves-heading-outside-track
 * ```js
 * const el = mount(`
 *   <ry-logo-bar layout="marquee">
 *     <p id="h">Trusted by</p>
 *     <span>A</span>
 *   </ry-logo-bar>
 * `);
 * const heading = el.querySelector('#h');
 * await expectEqual(!!heading, true);
 * // heading should come BEFORE the track
 * const track = el.querySelector('[data-ry-target="track"]');
 * const order = heading.compareDocumentPosition(track);
 * await expectEqual((order & Node.DOCUMENT_POSITION_FOLLOWING) !== 0, true);
 * ```
 *
 * @tutorial marquee-clones-are-aria-hidden
 * ```js
 * const el = mount(`
 *   <ry-logo-bar layout="marquee">
 *     <span>A</span>
 *   </ry-logo-bar>
 * `);
 * const track = el.querySelector('[data-ry-target="track"]');
 * // First child is original, rest are clones
 * await expectEqual(track.children[0].hasAttribute('aria-hidden'), false, 'original is announceable');
 * await expectAttr(track.children[1], 'aria-hidden', 'true');
 * await expectAttr(track.children[2], 'aria-hidden', 'true');
 * await expectAttr(track.children[3], 'aria-hidden', 'true');
 * ```
 *
 * @tutorial marquee-with-no-logos-is-noop
 * ```js
 * const el = mount('<ry-logo-bar layout="marquee"><p>Only heading</p></ry-logo-bar>');
 * await expectEqual(el.querySelector('[data-ry-target="track"]'), null, 'no track without logos');
 * ```
 *
 * @see ry-logo
 * @see ry-hero
 */

import { RyElement } from '../core/ry-element.js';

export class RyLogoBar extends RyElement {
  setup(): void {
    // Marquee: duplicate children for seamless loop
    if (this.getAttribute('layout') === 'marquee') {
      this.#setupMarquee();
    }
  }

  #setupMarquee(): void {
    // Wrap existing logo children (not <p>) in a track, then duplicate
    const heading = this.$('p');
    const logos = [...this.children].filter(c => c.tagName !== 'P');
    if (logos.length === 0) return;

    const track = document.createElement('div');
    track.setAttribute('data-ry-target', 'track');
    track.classList.add('ry-logo-bar__track');

    // Move originals into track
    logos.forEach(l => track.appendChild(l));

    // Clone 3x for seamless loop (4 sets total)
    for (let i = 0; i < 3; i++) {
      logos.forEach(l => {
        const clone = l.cloneNode(true) as HTMLElement;
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }

    // Re-append heading then track
    this.innerHTML = '';
    if (heading) this.appendChild(heading);
    this.appendChild(track);
  }
}

customElements.define('ry-logo-bar', RyLogoBar);
